import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Subscribe } from 'unstated';

import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Button from '@atlaskit/button/button';
import { token } from '@atlaskit/tokens';

import { InspectPermissionsDialog } from '@confluence/inspect-permissions/entry-points/InspectPermissionsDialog.ts';
import { RestrictionsDialogStateContainer } from '@confluence/restrictions-dialog-state-container';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { SPACE_PERMISSIONS_SETTINGS } from '@confluence/named-routes/entry-points/routes.ts';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { expVal } from '@confluence/feature-experiments';

import { i18n } from './i18n.ts';
import {
	FlexColumn,
	PermissionWarningContainer,
	Text,
	TextStrong,
	Wrapper,
} from './ContentOwnershipStyles.tsx';
import { PARENT_PAGE_LEARN_MORE_URL } from './contentOwnershipConstants.ts';
import { useIsSpaceOrSiteAdmin } from './useIsSpaceOrSiteAdmin.ts';
import { generateMailtoLink, useSPAViewContext } from './content-ownership-helpers.ts';
import { useContentOwnershipSpaceAdmins } from './useContentOwnershipSpaceAdmins.tsx';
import { useContentInfo } from './useContentInfo.tsx';
import type { OptionData } from '@confluence/user-and-group-search/entry-points/user-picker.ts';

type ContentOwnershipPermissionWarningProps = {
	isParentPageRestriction?: boolean;
	isSpaceRestricted?: boolean;
	isEditPageRestricted?: boolean;
	contentId: string;
	selectedUser?: OptionData;
	resetRestrictions: () => void;
};

const SpaceRestrictionButton = ({ intl, displayName, contentId, selectedUser }) => {
	const [spaceKey] = usePageSpaceKey();

	const isSpaceOrSiteAdmin = useIsSpaceOrSiteAdmin();
	const { isSiteAdmin } = useSPAViewContext();
	const { canManageUsers } = useSpaceManagementOperations({
		spaceKey: spaceKey ?? '',
		skip: !spaceKey || !expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false),
	});
	const canManageSpacePermissions = expVal(
		'permissions_rbac_dependencies_callsites',
		'isEnabled',
		false,
	)
		? canManageUsers || isSiteAdmin
		: isSpaceOrSiteAdmin;

	const spacePermissionsUrl = `${SPACE_PERMISSIONS_SETTINGS.toUrl({
		spaceKey,
		tab: SpacePermissionsTabKey.Users,
	})}`;

	const SpaceAdminRestrictionButton = () => {
		return (
			<Button appearance="link" href={spacePermissionsUrl} target="_blank">
				{intl.formatMessage(i18n.manageSpaceRestrictionButtonAdmin)}
			</Button>
		);
	};

	const SpaceUserRestrictionButton = () => {
		const spaceAdmins = useContentOwnershipSpaceAdmins();
		const { contentTitle } = useContentInfo({ contentId });

		const mailtoLink = generateMailtoLink({
			intl,
			spaceAdmins,
			spacePermissionsUrl,
			spaceKey,
			contentId,
			requestedUser: selectedUser?.name,
			currentUser: displayName,
			pageTitle: contentTitle,
		});

		return (
			<Button appearance="link" href={mailtoLink}>
				{intl.formatMessage(i18n.manageSpaceRestrictionButton)}
			</Button>
		);
	};

	return canManageSpacePermissions ? (
		<SpaceAdminRestrictionButton />
	) : (
		<SpaceUserRestrictionButton />
	);
};

const ParentPageRestrictionButton = ({ intl, contentId, isPremium }) => {
	const [showInspectPermissions, setShowInspectPermissions] = useState<boolean>(false);
	return (
		<>
			{isPremium ? (
				<Button appearance="link" onClick={() => setShowInspectPermissions(true)}>
					{intl.formatMessage(i18n.managePremiumRestrictionButton)}
				</Button>
			) : (
				<Button appearance="link" target="_blank" href={PARENT_PAGE_LEARN_MORE_URL}>
					{intl.formatMessage(i18n.manageParentPageRestrictionButton)}
				</Button>
			)}
			{showInspectPermissions && (
				<InspectPermissionsDialog
					contentId={contentId}
					onClose={() => setShowInspectPermissions(false)}
				/>
			)}
		</>
	);
};

const RestrictionButton = ({ intl, resetRestrictions }) => {
	return (
		<Subscribe to={[RestrictionsDialogStateContainer]}>
			{(dialogState: any) => (
				<Button
					appearance="link"
					onClick={() => {
						dialogState.setIsOpen(true);
						resetRestrictions();
					}}
				>
					{intl.formatMessage(i18n.manageRestrictionButton)}
				</Button>
			)}
		</Subscribe>
	);
};

export const ContentOwnershipPermissionWarning = ({
	isParentPageRestriction = false,
	isSpaceRestricted = false,
	isEditPageRestricted = false,
	contentId = '',
	selectedUser,
	resetRestrictions,
}: ContentOwnershipPermissionWarningProps): React.JSX.Element => {
	const intl = useIntl();
	const { edition, displayName } = useSessionData();
	const isPremium = edition === ConfluenceEdition.PREMIUM;

	// Set i18n title and message based on restrictionType
	let restrictionTitle = {};
	let restrictionMessage = {};
	if (isSpaceRestricted) {
		restrictionTitle = i18n.ownerSpaceRestrictionTitle;
		restrictionMessage = i18n.ownerSpaceRestrictionMessage;
	} else if (isPremium && isParentPageRestriction) {
		restrictionTitle = i18n.ownerPremiumRestrictionTitle;
		restrictionMessage = i18n.ownerPremiumRestrictionMessage;
	} else if (isParentPageRestriction) {
		restrictionTitle = i18n.ownerParentPageRestrictionTitle;
		restrictionMessage = i18n.ownerParentPageRestrictionMessage;
	} else if (isEditPageRestricted) {
		restrictionTitle = i18n.ownerRestrictionTitle;
		restrictionMessage = i18n.ownerRestrictionMessage;
	} else {
		restrictionTitle = i18n.ownerRestrictionTitle;
		restrictionMessage = i18n.ownerRestrictionMessage;
	}

	let restrictionButton: JSX.Element | null = null;
	if (isSpaceRestricted) {
		restrictionButton = (
			<SpaceRestrictionButton
				intl={intl}
				displayName={displayName}
				selectedUser={selectedUser}
				contentId={contentId}
			/>
		);
	} else if (isParentPageRestriction) {
		// Premium link renders inspect permissions dialog
		restrictionButton = (
			<ParentPageRestrictionButton intl={intl} contentId={contentId} isPremium={isPremium} />
		);
	} else {
		// Default opens permissions dialog
		restrictionButton = <RestrictionButton intl={intl} resetRestrictions={resetRestrictions} />;
	}

	return (
		<PermissionWarningContainer data-testid="content-ownership-permissions-warning-container">
			<Wrapper>
				<StatusWarningIcon spacing="spacious" label="" color={token('color.text.warning')} />
				<FlexColumn>
					<TextStrong>{intl.formatMessage(restrictionTitle)}</TextStrong>
					<Text>{intl.formatMessage(restrictionMessage)}</Text>
					{restrictionButton}
				</FlexColumn>
			</Wrapper>
		</PermissionWarningContainer>
	);
};
